import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@coolremote/shared';
import { t } from 'ttag';
import useStyles from '../FilterOutSpecificErrorCodesForm.styles';
import useTypes from '@hooks/useTypes';
import SimpleSelect from '../../SimpleSelect';
import { DeleteIcon } from 'src/logos';
import AutoResizeTextarea from './AutoResizeTextArea';
import clsx from 'clsx';


/**
 * An array of unit type options used for filtering or selection purposes.
 * Each option contains a text label and a corresponding value.
 *
 * @constant {Array<Object>} unitTypeOptions
 * @property {string} text - The display text for the unit type option, localized using the `t` function.
 * @property {string} value - The value representing the unit type, e.g., 'indoor' or 'outdoor'.
 */
const unitTypeOptions = [
  { text: t`Indoor`, value: 'indoor' },
  { text: t`Outdoor`, value: 'outdoor' },
  { text: t`Both`, value: 'both' },
];

/**
 * @typedef {Object} Filter
 * @property {string} brand - The brand associated with the filter.
 * @property {string} codes - The error codes for the filter, comma-separated.
 * @property {string} type - The unit type for the filter, e.g., 'indoor' or 'outdoor'.
 */

/**
 * FiltersList component renders a list of filters with options to update or delete them.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array<Filter>} props.filters - The list of filters to display.
 * @param {Function} props.onFilterDelete - Callback function to handle filter deletion.
 * @param {boolean} props.disabled - Flag to disable the inputs and actions.
 * @param {Object} props.validity - Object containing validation details for the filters.
 * @returns {JSX.Element} The rendered FiltersList component.
 */
const FiltersList = ({ filters = [], onFilterDelete, disabled = false, validity }) => {
  const { hvacBrands } = useTypes();

  const brandOptions = useMemo(
    () => hvacBrands.map(({ value, name }) => ({ value, text: name })),
    [hvacBrands]
  );

  const classes = useStyles();

  const {details:ValidationDetails = []} = validity;

  const handleDeleteFilter = (index) => () => {
    onFilterDelete(index);
  };

  const updateFilter = (index, key) => ({ target }) => {
    const { value } = target;
    const newFilters = [...filters];
    newFilters[index][key] = value;
    onFilterDelete(newFilters);
  };

  const computedFilters = Array.isArray(filters) ? filters : [];

  if(computedFilters.length === 0) {
    return null;
  }

  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{t`Brand`}</th>
            <th>{t`Codes`}</th>
            <th>{t`Unit Type`}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {computedFilters.map((filter, index) => (
            <tr key={index}>
              <td
                className={clsx(classes.tableCell, {
                  error: ValidationDetails[index]?.brand === false,
                })}
              >
                <SimpleSelect
                  className={classes.select}
                  variant="outlined"
                  options={brandOptions}
                  value={filter.brand}
                  onChange={updateFilter(index, 'brand')}
                  placeholder={t`Select Brand`}
                  disabled={disabled}
                />
              </td>
              <td
                className={clsx(classes.tableCell, {
                  error: ValidationDetails[index]?.codes === false,
                })}
              >
                <AutoResizeTextarea
                  value={filter.codes}
                  onChange={updateFilter(index, 'codes')}
                  disabled={disabled}
                  placeholder={t`Insert codes comma separated (e.g. 121,E7,34)`}
                  className={classes.inputField}
                ></AutoResizeTextarea>
              </td>
              <td
                className={clsx(classes.tableCell, {
                  error: ValidationDetails[index]?.type === false,
                })}
              >
                <SimpleSelect
                  className={classes.select}
                  variant="outlined"
                  options={unitTypeOptions}
                  value={filter.type}
                  onChange={updateFilter(index, 'type')}
                  placeholder={t`Unit type`}
                  disabled={disabled}
                />
              </td>
              <td className={classes.tableCell}>
                <IconButton
                  aria-label="delete"
                  onClick={handleDeleteFilter(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

FiltersList.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      brand: PropTypes.string,
      codes: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  onFilterDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

FiltersList.defaultProps = {
  filters: [],
  disabled: false,
};

export default FiltersList;
